import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PublicNav, PublicFooter } from "./PublicHome";
import { READINESS_BADGE, TYPE_LABEL, priceLine, useWorkforceCta, CtaButton, SolutionCard } from "../components/WorkforceCta";
import { ArrowLeft } from "lucide-react";

export default function WorkforceSolution() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [nf, setNf] = useState(false);
  const { handleCta, modal, checkoutMsg } = useWorkforceCta();
  useEffect(() => { setS(null); setNf(false); api.get(`/public/workforce/${slug}`).then(r => setS(r.data)).catch(() => setNf(true)); }, [slug]);

  if (nf) return <Shell><div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="ws-not-found"><h1 className="font-display text-2xl font-bold">Workforce solution not found</h1><Link to="/workforce" className="text-teal-700 text-sm mt-3 inline-block">← Back to the Workforce Marketplace</Link></div></Shell>;
  if (!s) return <Shell><div className="max-w-3xl mx-auto px-6 py-24 text-sm text-slate-500">Loading…</div></Shell>;
  const badge = READINESS_BADGE[s.readiness] || READINESS_BADGE.INACTIVE;
  const isDept = s.solution_type === "AI_DEPARTMENT";

  return (
    <Shell>
      <section className="bg-slate-950 text-white">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
          <Link to="/workforce" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="ws-back"><ArrowLeft size={12}/> Workforce Marketplace</Link>
          <div className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-teal-300">{s.provider} · {TYPE_LABEL[s.solution_type] || s.solution_type}{s.department ? ` · ${s.department}` : ""}</div>
          <h1 className="font-display font-bold text-3xl md:text-5xl tracking-tight mt-2" data-testid="ws-detail-name">{s.name}</h1>
          <p className="text-slate-300 text-base md:text-lg mt-4 max-w-2xl">{s.short}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded border ${badge.cls}`} data-testid="ws-detail-status">{badge.label}</span>
            <span className="font-mono text-sm" data-testid="ws-detail-price">{priceLine(s)}</span>
            {s.requires_active && <span className="text-[11px] text-amber-300">Requires active {s.requires_active === "SALESHUB_ANY" ? "SalesHub AI Pro" : s.requires_active} subscription</span>}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_320px] gap-10">
        <div className="space-y-8">
          {s.full_description && <Block title={isDept ? "Department purpose" : `What this ${TYPE_LABEL[s.solution_type] || "workforce solution"} does`}><p className="text-sm text-slate-700 whitespace-pre-line">{s.full_description}</p></Block>}
          {(s.responsibilities || []).length > 0 && <Block title="Responsibilities"><List items={s.responsibilities}/></Block>}
          {(s.outcomes || []).length > 0 && <Block title="Primary outcomes"><List items={s.outcomes}/></Block>}
          {s.best_for && <Block title="Best for"><p className="text-sm text-slate-700">{s.best_for}</p></Block>}
          {(s.reports_to || (s.works_with || []).length > 0) && (
            <div className="grid sm:grid-cols-2 gap-6">
              {s.reports_to && <Block title="Reports to"><p className="text-sm text-slate-700" data-testid="ws-reports-to">{s.reports_to}</p></Block>}
              {(s.works_with || []).length > 0 && <Block title="Works with"><List items={s.works_with}/></Block>}
            </div>
          )}
          {s.deployment && <Block title="Deployment · How it works"><p className="text-sm text-slate-700 whitespace-pre-line">{s.deployment}</p></Block>}
          {(s.bundle_members || []).length > 0 && (
            <Block title="Included AI Employees"><div className="grid sm:grid-cols-2 gap-4">{s.bundle_members.map(m => <SolutionCard key={m.sku} s={m} onCta={handleCta} checkoutMsg={checkoutMsg}/>)}</div></Block>
          )}
          {(s.recommended_products || []).length > 0 && <Block title="Recommended MPS products"><List items={s.recommended_products}/></Block>}
        </div>
        <aside className="lg:sticky lg:top-6 self-start rounded-xl border border-slate-200 bg-white shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Pricing</div>
          <div className="font-mono text-xl text-slate-900 mt-1">{priceLine(s)}</div>
          {s.setup_fee && <div className="text-xs text-slate-500 mt-1">${Number(s.setup_fee).toLocaleString()} one-time setup</div>}
          {s.usage_billing && <div className="text-xs text-slate-500 mt-1">Usage-based billing applies</div>}
          <div className="mt-4"><CtaButton s={s} onClick={handleCta} checkoutMsg={checkoutMsg} size="lg"/></div>
          <p className="text-[11px] text-slate-500 mt-3">AI workforce solutions are managed AI services, not human employees. Human approval is retained on outbound actions.</p>
        </aside>
      </section>
      {modal}
    </Shell>
  );
}

function Shell({ children }) { return <div className="min-h-screen bg-white"><PublicNav/><main>{children}</main><PublicFooter/></div>; }
function Block({ title, children }) { return <div><h2 className="font-display font-bold text-base md:text-lg text-slate-900 mb-2">{title}</h2>{children}</div>; }
function List({ items }) { return <ul className="space-y-1.5 text-sm text-slate-700">{items.map(i => <li key={i} className="flex gap-2"><span className="text-teal-600">✓</span>{i}</li>)}</ul>; }
